import type { Dictionary } from '@/shared/legacy/contexts/LocalizationContext/types';
import { dictionary as en } from '@/shared/legacy/contexts/LocalizationContext/languages/en';
import { dictionary as nl } from '@/shared/legacy/contexts/LocalizationContext/languages/nl';
import { useI18nStore, type Language } from './i18n.store';

/**
 * Both dictionaries, so an overriding surface can switch without waiting for the context to
 * re-render. Imported rather than pushed in: `shared/legacy/` is part of the shared layer, and
 * the store already takes the `Dictionary` type from this same module.
 *
 * Every lookup below falls back to the context-fed dictionary. The store already refuses an
 * override it cannot render, so this is belt and braces — but the cost of being wrong is a
 * `undefined.someKey` on a public candidate page, which is a white screen rather than the blank
 * string a missing dictionary would otherwise give.
 */
const DICTIONARIES: Record<Language, Dictionary> = { nl, en };

/**
 * The copy strings to render.
 *
 * When a surface-scoped `override` is set the matching dictionary is returned directly;
 * otherwise this is the context-fed one, unchanged. See `useLanguage` and the store.
 */
export const useDictionary = () => {
  return useI18nStore((s) => (s.override ? DICTIONARIES[s.override] ?? s.dictionary : s.dictionary));
};

export const getDictionary = () => {
  const { override, dictionary } = useI18nStore.getState();

  return (override ? DICTIONARIES[override] : undefined) ?? dictionary;
};
